import React, { useEffect, useMemo, useState } from 'react';
import { purchasePaymentsService } from '../services/purchasePaymentsService';
import { suppliersService } from '../services/suppliersService';
import TreasuryAccountSelector from '../components/TreasuryAccountSelector';
import { Button, Badge, Input, Select, Textarea, Label, Icon } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import {
  IconCash, IconSync, IconPlus, IconMagic, IconEdit, IconTrash, IconX,
  IconChevronLeft, IconChevronRight, IconFileExport,
} from '@tabler/icons-react';
import ExportModal from '../components/ExportModal';

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((toNumber(n) + Number.EPSILON) * 100) / 100;

const emptyPayment = {
  supplierId: '',
  paymentDate: new Date().toISOString().split('T')[0],
  currency: 'TND',
  method: 'BANK_TRANSFER',
  reference: '',
  amount: 0,
  status: 'DRAFT',
  notes: '',
  allocations: [], // { purchaseInvoiceId, allocatedAmount }
  treasuryAccountId: '',
};

export default function PurchasePayments() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [search, setSearch] = useState('');
  const [filterSupplierId, setFilterSupplierId] = useState('');

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [form, setForm] = useState(emptyPayment);

  const [openInvoices, setOpenInvoices] = useState([]); // factures ouvertes du fournisseur sélectionné

  const supplierById = useMemo(() => {
    const map = new Map();
    suppliers.forEach((s) => map.set(s._id || s.id, s));
    return map;
  }, [suppliers]);

  const closeModal = () => {
    setShowModal(false);
    setEditingPayment(null);
    setForm(emptyPayment);
    setOpenInvoices([]);
    setError('');
  };

  const openCreate = () => {
    setEditingPayment(null);
    setForm(emptyPayment);
    setOpenInvoices([]);
    setError('');
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingPayment(p);
    setForm({
      supplierId: p?.supplierId || '',
      paymentDate: p?.paymentDate || new Date().toISOString().split('T')[0],
      currency: p?.currency || 'TND',
      method: p?.method || 'BANK_TRANSFER',
      reference: p?.reference || '',
      amount: p?.amount ?? 0,
      status: p?.status || 'DRAFT',
      notes: p?.notes || '',
      allocations: Array.isArray(p?.allocations) ? p.allocations.map((a) => ({
        purchaseInvoiceId: a?.purchaseInvoiceId,
        allocatedAmount: a?.allocatedAmount ?? 0,
      })) : [],
     treasuryAccountId: p?.treasuryAccountId || '',
    });
    setOpenInvoices([]);
    setError('');
    setShowModal(true);
  };

  const loadSuppliers = async () => {
    try {
      const data = await suppliersService.list();
      setSuppliers(normalizeListResponse(data));
    } catch {
      setSuppliers([]);
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await purchasePaymentsService.list();
      setPayments(normalizeListResponse(data));
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Backend non disponible pour le module Achats (Règlements).";
      setError(msg);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const [showExportModal, setShowExportModal] = useState(false);

  const purchasePaymentColumns = [
    { key: 'supplier', label: 'Fournisseur', accessor: (p) => { const s = supplierById.get(p?.supplierId); return s?.name || '—'; } },
    { key: 'paymentDate', label: 'Date', accessor: (p) => p?.paymentDate || '—' },
    { key: 'method', label: 'Méthode', accessor: (p) => p?.method || '—' },
    { key: 'reference', label: 'Référence', accessor: (p) => p?.reference || '—' },
    { key: 'amount', label: 'Montant', accessor: (p) => `${round2(p?.amount || 0).toFixed(2)} DT` },
    { key: 'status', label: 'Statut', accessor: (p) => p?.status || '—' },
  ];

  useEffect(() => {
    loadSuppliers();
    loadPayments();
  }, []);

  const filteredPayments = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = payments;
    if (filterSupplierId) result = result.filter((p) => String(p?.supplierId) === String(filterSupplierId));
    if (!q) return result;
    return result.filter((p) => {

    const supplier = supplierById.get(p?.supplierId);
      const supplierName = (supplier?.name || '').toLowerCase();
      const ref = (p?.reference || '').toLowerCase();
      const method = (p?.method || '').toLowerCase();
      const status = (p?.status || '').toLowerCase();
      return supplierName.includes(q) || ref.includes(q) || method.includes(q) || status.includes(q);
    });
  }, [payments, search, filterSupplierId, supplierById]);

  useEffect(() => { setPage(1); }, [payments.length]);

  const totalPages = Math.ceil(filteredPayments.length / PAGE_SIZE) || 1;
  const paginatedPayments = filteredPayments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const allocatedTotal = useMemo(() => {
    return round2((form.allocations || []).reduce((sum, a) => sum + toNumber(a.allocatedAmount), 0));
  }, [form.allocations]);

  const unallocatedAmount = useMemo(() => {
    return round2(toNumber(form.amount) - allocatedTotal);
  }, [form.amount, allocatedTotal]);

  const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const loadOpenInvoices = async (supplierId) => {
    if (!supplierId) {
      setOpenInvoices([]);
      return;
    }
    try {
      const data = await purchasePaymentsService.listOpenInvoices(supplierId);
      setOpenInvoices(normalizeListResponse(data));
    } catch {
      setOpenInvoices([]);
    }
  };

  const handleSupplierChange = async (supplierId) => {
    setField('supplierId', supplierId);
    // reset allocations lors du changement fournisseur
    setField('allocations', []);
    await loadOpenInvoices(supplierId);
  };

  const autoAllocate = () => {
    setError('');

    const amount = round2(toNumber(form.amount));
    if (amount <= 0) {
      setError('Veuillez saisir un montant de paiement > 0.');
      return;
    }
    if (!form.supplierId) {
      setError('Veuillez sélectionner un fournisseur.');
      return;
    }
    if (!openInvoices.length) {
      setError("Aucune facture ouverte à régler (ou backend /purchase-invoices/open indisponible).");
      return;
    }

    let remaining = amount;
    const allocations = [];

    // oldest first (supposé déjà trié par le backend; sinon on trie par invoiceDate)
    const sorted = [...openInvoices].sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate));

    for (const inv of sorted) {
      if (remaining <= 0) break;

      const invoiceId = inv?._id || inv?.id;
      const remainingToPay = round2(toNumber(inv?.remainingToPay));

      if (!invoiceId) continue;
      if (remainingToPay <= 0) continue;

      const alloc = round2(Math.min(remaining, remainingToPay));
      if (alloc > 0) {
        allocations.push({ purchaseInvoiceId: invoiceId, allocatedAmount: alloc });
        remaining = round2(remaining - alloc);
      }
    }

    setField('allocations', allocations);
  };

  const updateAllocation = (invoiceId, newAmount) => {
    const amt = round2(toNumber(newAmount));
    setForm((p) => {
      const allocations = [...(p.allocations || [])];
      const idx = allocations.findIndex((a) => a.purchaseInvoiceId === invoiceId);
      if (idx === -1) allocations.push({ purchaseInvoiceId: invoiceId, allocatedAmount: amt });
      else allocations[idx] = { ...allocations[idx], allocatedAmount: amt };
      return { ...p, allocations };
    });
  };

  const validate = () => {
    if (!form.supplierId) return 'Veuillez sélectionner un fournisseur.';
    if (!form.paymentDate) return 'La date de paiement est obligatoire.';
    if (round2(toNumber(form.amount)) <= 0) return 'Le montant doit être > 0.';
    if (!form.method) return 'Veuillez sélectionner un mode de paiement.';
    if (allocatedTotal > round2(toNumber(form.amount))) return 'Le total alloué ne doit pas dépasser le montant du paiement.';
    return '';
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  const v = validate();
  if (v) {
    setError(v);
    return;
  }
  setLoading(true);
  setError('');
  try {
    const payload = {
      supplierId: form.supplierId,
      paymentDate: form.paymentDate,
      currency: form.currency || 'TND',
      method: form.method,
      reference: form.reference?.trim() || undefined,
      amount: round2(toNumber(form.amount)),
      status: form.status || 'DRAFT',
      notes: form.notes?.trim() || undefined,
      allocations: (form.allocations || [])
        .filter((a) => a.purchaseInvoiceId && round2(toNumber(a.allocatedAmount)) > 0)
        .map((a) => ({
          purchaseInvoiceId: a.purchaseInvoiceId,
          allocatedAmount: round2(toNumber(a.allocatedAmount)),
        })),
      treasuryAccountId: form.treasuryAccountId,  // ✅ AJOUTER CETTE LIGNE
    };
    const id = editingPayment?._id || editingPayment?.id;
    if (id) await purchasePaymentsService.update(id, payload);
    else await purchasePaymentsService.create(payload);
    closeModal();
    await loadPayments();
  } catch (e2) {
    const msg = e2?.response?.data?.message || e2?.message || "Erreur lors de l'enregistrement.";
    setError(msg);
  } finally {
    setLoading(false);
  }
};

  const handleDelete = async (p) => {
    const id = p?._id || p?.id;
    if (!id) {
      setError('Impossible de supprimer: identifiant manquant.');
      return;
    }
    if (!await showConfirm('Êtes-vous sûr de vouloir supprimer ce règlement ?')) return;
    setLoading(true);
    setError('');
    try {
      await purchasePaymentsService.remove(id);
      await loadPayments();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Erreur lors de la suppression.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const allocationByInvoiceId = useMemo(() => {
    const map = new Map();
    (form.allocations || []).forEach((a) => map.set(a.purchaseInvoiceId, a.allocatedAmount));
    return map;
  }, [form.allocations]);

  const selectedSupplier = supplierById.get(form.supplierId);

  return (
    <div className="flex-1 p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Règlements achats</h1>
        <p className="text-gray-600">Enregistrez les paiements fournisseurs et répartissez-les sur les factures.</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Liste des règlements</h3>
          <p className="text-sm text-gray-500">{filteredPayments.length} règlement(s)</p>
        </div>

        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} disabled={filteredPayments.length === 0}>
            <IconFileExport size={15} strokeWidth={1.6} className="mr-1" /> Export
          </Button>
          <Button variant="secondary" size="sm" onClick={loadPayments} disabled={loading}>
            <Icon name="IconSync" size={14} className="mr-1" /> Rafraîchir
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <Icon name="IconPlus" size={14} className="mr-1" /> Nouveau règlement
          </Button>
        </div>
      </div>

      {!!error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800 font-semibold">Erreur</div>
          <div className="text-sm text-red-700 mt-1">{error}</div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h4 className="font-semibold text-gray-800">Recherche</h4>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Filtrer par fournisseur</Label>
              <Select
                value={filterSupplierId}
                onChange={(e) => { setFilterSupplierId(e.target.value); setPage(1); }}
              >
                <option value="">-- Tous les fournisseurs --</option>
                {suppliers.map((s) => {
                  const id = s._id || s.id;
                  return <option key={id} value={id}>{s.name} {s.mf ? `(${s.mf})` : ''}</option>;
                })}
              </Select>
            </div>
            <div>
              <Label>Référence / Méthode / Statut</Label>
              <Input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Ex: chèque 145, bank_transfer, posted..."
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
            <p className="mt-2 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Fournisseur</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Méthode</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Référence</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Montant</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Statut</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedPayments.map((p) => {
                  const id = p?._id || p?.id;
                  const supplier = supplierById.get(p?.supplierId);
                  return (
                    <tr key={id || p?.paymentDate} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <div className="font-medium text-gray-900 text-sm truncate max-w-[240px]">
                          {supplier?.name || '—'}
                        </div>
                        <div className="text-xs text-gray-500 font-mono truncate max-w-[240px]">
                          {supplier?.mf || ''}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm text-gray-800">{p?.paymentDate || '—'}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs font-mono text-gray-800">{p?.method || '—'}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm text-gray-800 truncate max-w-[220px]">{p?.reference || '—'}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-semibold text-gray-900 text-sm">{round2(toNumber(p?.amount)).toFixed(2)} DT</div>
                      </td>
                      <td className="p-3">
                        <Badge variant="gray">{p?.status || '—'}</Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button variant="secondary" size="sm" onClick={() => openEdit(p)} title="Modifier">
                            <Icon name="IconEdit" size={14} />
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => handleDelete(p)} title="Supprimer">
                            <Icon name="IconTrash" size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!loading && filteredPayments.length === 0 && (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Icon name="IconCash" size={24} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun règlement</h3>
                <p className="text-gray-500 mb-6">Créez votre premier règlement fournisseur.</p>
                <Button variant="primary" size="md" onClick={openCreate}>
                  <Icon name="IconPlus" size={16} className="mr-1" /> Nouveau règlement
                </Button>
              </div>
            )}
          </div>
        )}
        {!loading && filteredPayments.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {filteredPayments.length} règlement(s) — Page {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronLeft size={16} strokeWidth={1.6} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  {editingPayment ? 'Modifier le règlement' : 'Nouveau règlement'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Répartition automatique sur les factures les plus anciennes.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeModal} aria-label="Fermer" type="button">
                <Icon name="IconX" size={20} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {!!error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-800 font-semibold">Erreur</div>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label required>Fournisseur</Label>
                    <Select
                      value={form.supplierId}
                      onChange={(e) => handleSupplierChange(e.target.value)}
                    >
                      <option value="">-- Sélectionner --</option>
                      {suppliers.map((s) => {
                        const id = s._id || s.id;
                        return (
                          <option key={id} value={id}>
                            {s.name} {s.mf ? `(${s.mf})` : ''}
                          </option>
                        );
                      })}
                    </Select>
                    {selectedSupplier && (
                      <p className="text-xs text-gray-500 mt-1">
                        MF: <span className="font-mono">{selectedSupplier.mf || '—'}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <Label required>Date paiement</Label>
                    <Input
                      type="date"
                      value={form.paymentDate}
                      onChange={(e) => setField('paymentDate', e.target.value)}
                    />
                  </div>

                  <div>
                    <Label required>Montant (DT)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setField('amount', e.target.value)}
                    />
                  </div>

                  <div>
                    <Label required>Méthode</Label>
                    <Select
                      value={form.method}
                      onChange={(e) => setField('method', e.target.value)}
                    >
                      <option value="CASH">CASH</option>
                      <option value="BANK_TRANSFER">BANK_TRANSFER</option>
                      <option value="CHECK">CHECK</option>
                      <option value="CARD">CARD</option>
                      <option value="WITHHOLDING_TAX">Retenue à la source (RS)</option>
                      <option value="OTHER">OTHER</option>
                    </Select>
                  </div>

                  {/* ✅ SÉLECTEUR DE COMPTE TRÉSORERIE - pas pour RS */}
                  {form.method !== 'WITHHOLDING_TAX' && <div>
                    <TreasuryAccountSelector
                      paymentMethod={form.method}
                      selectedAccountId={form.treasuryAccountId}
                      onChange={(accountId) => setField('treasuryAccountId', accountId)}
                    />
                  </div>}

                  <div>
                    <Label>Référence</Label>
                    <Input
                      value={form.reference}
                      onChange={(e) => setField('reference', e.target.value)}
                      placeholder="N° chèque / ref virement..."
                    />
                  </div>

                  <div>
                    <Label>Statut</Label>
                    <Select
                      value={form.status}
                      onChange={(e) => setField('status', e.target.value)}
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="POSTED">POSTED</option>
                      <option value="CANCELED">CANCELED</option>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between bg-gray-50 border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Répartition</div>
                    <div className="text-xs text-gray-600">
                      Total alloué: <span className="font-semibold">{allocatedTotal.toFixed(2)} DT</span> — Non affecté:{' '}
                      <span className={`font-semibold ${unallocatedAmount < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {unallocatedAmount.toFixed(2)} DT
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" type="button" onClick={autoAllocate}>
                      <Icon name="IconMagic" size={14} className="mr-1" /> Répartir automatiquement
                    </Button>
                    <Button variant="secondary" size="sm" type="button" onClick={() => loadOpenInvoices(form.supplierId)}>
                      <Icon name="IconSync" size={14} className="mr-1" /> Charger factures ouvertes
                    </Button>
                  </div>
                </div>

                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold text-gray-800">Factures ouvertes</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Source: <span className="font-mono">GET /purchase-invoices/open?supplierId=...</span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Date</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">N° facture</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Reste à payer</th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">Montant alloué</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {openInvoices.map((inv) => {
                          const id = inv?._id || inv?.id;
                          const allocated = allocationByInvoiceId.get(id) ?? 0;
                          return (
                            <tr key={id} className="hover:bg-gray-50">
                              <td className="p-3 text-sm text-gray-800">{inv?.invoiceDate || '—'}</td>
                              <td className="p-3 text-xs font-mono text-gray-800">{inv?.supplierInvoiceNumber || '—'}</td>
                              <td className="p-3 text-sm text-gray-800">
                                {round2(toNumber(inv?.remainingToPay)).toFixed(2)} DT
                              </td>
                              <td className="p-3">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={allocated}
                                  onChange={(e) => updateAllocation(id, e.target.value)}
                                />
                                <p className="text-[11px] text-gray-500 mt-1">
                                  Max conseillé: {round2(toNumber(inv?.remainingToPay)).toFixed(2)} DT
                                </p>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {openInvoices.length === 0 && (
                      <div className="p-6 text-sm text-gray-600">
                        Aucune facture ouverte chargée. Sélectionne un fournisseur puis clique “Charger factures ouvertes”.
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)}
                    placeholder="Remarques internes..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="secondary" type="button" onClick={closeModal} disabled={loading}>
                    Annuler
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          columns={purchasePaymentColumns}
          data={filteredPayments}
          title="Liste des règlements achats"
          filename="reglements_achats"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
